/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

const FETCH_ORDER_LINE_ITEMS = gql`
  query FetchOrderLineItems($input: SearchInput) {
    search_menu_orders(input: $input) {
      menu_order_listing {
        order_reference
        line_item {
          name
          variant_name
          price
          overall_price
          quantity
          sold_out
          special_request
          modifier {
            name
            variant_name
            price_per_item
            quantity
          }
        }
      }
    }
  }
`;

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toModifier = raw => ({
  name: raw?.name ?? '',
  variantName: raw?.variant_name ?? null,
  pricePerItem: raw?.price_per_item ?? 0,
  quantity: raw?.quantity ?? 1,
});

const toLineItem = raw => ({
  name: raw?.name ?? '',
  variantName: raw?.variant_name ?? null,
  price: raw?.price ?? 0,
  overallPrice: raw?.overall_price ?? 0,
  quantity: raw?.quantity ?? 1,
  soldOut: raw?.sold_out ?? null,
  specialRequest: raw?.special_request ?? null,
  modifiers: Array.isArray(raw?.modifier)
    ? raw.modifier.filter(Boolean).map(toModifier)
    : [],
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated'))
      return { message: err.message, code: 'UNAUTHORIZED' };
    if (msg.includes('network') || msg.includes('failed to fetch'))
      return { message: err.message, code: 'NETWORK_ERROR' };
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function fetchOrderLineItems(
  params,
  client = apolloClient.clientPrivate,
) {
  const { userId, orderReference } = params;

  try {
    const { data } = await client.query({
      query: FETCH_ORDER_LINE_ITEMS,
      variables: {
        input: {
          user_id: userId,
          filter: {
            menu_order_filter: {
              order_reference: orderReference,
            },
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    const result = data?.search_menu_orders;

    if (!result) {
      throw { message: 'No data returned from search_menu_orders', code: 'PARSE_ERROR' };
    }

    const order = result.menu_order_listing?.[0];
    const rawItems = order?.line_item;

    if (!Array.isArray(rawItems)) {
      throw { message: 'No line items returned', code: 'PARSE_ERROR' };
    }

    return rawItems.filter(Boolean).map(toLineItem);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

export const fetchOrderLineItemsService = { fetchOrderLineItems };
